import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import { useAppServices } from "../../../hook/services";
import { toast } from "react-toastify";
import {
  Gear,
  Info,
  Lightning,
  CheckCircle,
  Phone,
  MagnifyingGlass,
} from "@phosphor-icons/react";
import { useAppInfo } from "../../../context/app";
import LoadingScreen from "../../../components/LoadingScreen";

const selectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: "3rem",
    borderRadius: "0.65rem",
    borderColor: state.isFocused ? "#2563eb" : "#dee2e6",
    boxShadow: state.isFocused ? "0 0 0 0.2rem rgba(37,99,235,0.15)" : "none",
    "&:hover": { borderColor: "#2563eb" },
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected
      ? "#2563eb"
      : state.isFocused
      ? "#eff6ff"
      : "white",
    color: state.isSelected ? "white" : "#111827",
    cursor: "pointer",
  }),
  menu: (base) => ({ ...base, borderRadius: "0.65rem", overflow: "hidden" }),
  menuPortal: (base) => ({ ...base, zIndex: 2000 }),
};

const Configuration = () => {
  const { appId, locationId, companyId } = useParams();
  const AppService = useAppServices();
  const [app] = useAppInfo();
  const [appInstall, setAppInstall] = useState({});
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [webhookSetupLoading, setWebhookSetupLoading] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [phoneNumbersLoading, setPhoneNumbersLoading] = useState(false);
  const [numberSearch, setNumberSearch] = useState("");
  const [assignModalNumber, setAssignModalNumber] = useState(null);
  const [assignForm, setAssignForm] = useState({
    useAsDefaultUserNumber: false,
    assignedUsers: [],
    useAsDefaultWorkflowNumber: false,
  });
  const [savingAssignment, setSavingAssignment] = useState(false);

  const openAssignModal = (num) => {
    setAssignModalNumber(num);
    const existing = formData.number_assignments?.[num.value] || {};
    setAssignForm({
      useAsDefaultUserNumber: formData.from_number === num.value,
      assignedUsers: existing.assignedUsers || [],
      useAsDefaultWorkflowNumber:
        formData.default_workflow_number === num.value,
    });
  };

  const handleAssignSave = async () => {
    const numberValue = assignModalNumber.value;
    const updated = { ...formData };

    if (assignForm.useAsDefaultUserNumber) {
      updated.from_number = numberValue;
    } else if (updated.from_number === numberValue) {
      updated.from_number = "";
    }

    if (assignForm.useAsDefaultWorkflowNumber) {
      updated.default_workflow_number = numberValue;
    } else if (updated.default_workflow_number === numberValue) {
      updated.default_workflow_number = "";
    }

    updated.number_assignments = {
      ...(updated.number_assignments || {}),
      [numberValue]: {
        assignedUsers: assignForm.assignedUsers,
      },
    };

    setFormData(updated);
    setSavingAssignment(true);
    const { response } = await AppService.app_installs.update({
      payload: { _id: appInstall?._id, credentials: updated },
    });
    setSavingAssignment(false);
    if (response) {
      toast.success("Number settings saved!");
      setAssignModalNumber(null);
    }
  };

  const loadLocationUsers = async (search) => {
    const usersRes = await AppService.quo.get_location_users({
      appId,
      query: `locationId=${locationId}&companyId=${companyId}&limit=50${
        search ? `&search=${encodeURIComponent(search)}` : ""
      }`,
    });
    return usersRes?.response?.data || [];
  };

  const fetchPhoneNumbers = async () => {
    setPhoneNumbersLoading(true);
    const phoneRes = await AppService.quo.get_phone_numbers({
      appId,
      query: `locationId=${locationId}&companyId=${companyId}`,
    });
    if (phoneRes?.response) setPhoneNumbers(phoneRes.response.data || []);
    setPhoneNumbersLoading(false);
  };

  const handleApiKeyBlur = async () => {
    const apiKey = formData.api_key;
    if (!apiKey) return;
    setPhoneNumbersLoading(true);
    const phoneRes = await AppService.quo.preview_phone_numbers({
      appId,
      payload: {
        extras: { locationId, companyId },
        data: { api_key: apiKey },
      },
    });
    if (phoneRes?.response) setPhoneNumbers(phoneRes.response.data || []);
    setPhoneNumbersLoading(false);
  };

  useEffect(() => {
    const loadData = async () => {
      const installRes = await AppService.app_installs.filter({
        query: `appId=${appId}&locationId=${locationId}`,
      });
      if (installRes?.response) {
        setAppInstall(installRes.response.data);
        setFormData(installRes.response.data?.credentials || {});

        if (installRes.response.data?.credentials?.api_key) {
          fetchPhoneNumbers();
        }
      }
      setLoading(false);
    };
    loadData();
  }, []);

  const handleChange = (e) =>
    setFormData({ ...formData, [e.target.name]: e.target.value });

  const handlePhoneSelect = (value) =>
    setFormData({ ...formData, from_number: value || "" });

  const toggleNumberDisabled = (numberValue) =>
    setFormData((prev) => ({
      ...prev,
      disabled_numbers: {
        ...(prev.disabled_numbers || {}),
        [numberValue]: !prev.disabled_numbers?.[numberValue],
      },
    }));

  const ensureWebhook = async () => {
    setWebhookSetupLoading(true);
    const res = await AppService.quo.ensure_webhook({
      appId,
      payload: { extras: { locationId, companyId } },
    });
    setWebhookSetupLoading(false);
    if (res?.response?.success && res?.response?.data) {
      const { webhookId, created, patched, deletedLegacy } = res.response.data;
      setFormData((prev) => ({ ...prev, webhook_id: webhookId }));
      if (deletedLegacy && created) {
        toast.success("Replaced older webhook with one covering all events!");
      } else if (created) {
        toast.success("Webhook created!");
      } else if (patched) {
        toast.success("Existing webhook updated to receive all events!");
      } else {
        toast.success("Webhook already fully configured!");
      }
    } else {
      toast.error("Failed to set up the webhook.");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      _id: appInstall?._id,
      credentials: formData,
    };
    const { response } = await AppService.app_installs.update({ payload });
    if (response) {
      toast.success("Configuration saved!");
      if (formData.api_key) fetchPhoneNumbers();
      if (formData.api_key && !formData.webhook_id) ensureWebhook();
    }
    setSaving(false);
  };

  if (loading) {
    return <LoadingScreen loadingText={"Loading data..."} />;
  }

  const phoneOptions = phoneNumbers.map((num) => ({
    value: num.value,
    label: num.label,
  }));
  const selectedPhoneOption =
    phoneOptions.find((o) => o.value === formData.from_number) || null;
  const filteredPhoneNumbers = phoneNumbers.filter((num) => {
    const query = numberSearch.trim().toLowerCase();
    if (!query) return true;
    return (
      num.name?.toLowerCase().includes(query) ||
      num.formattedNumber?.toLowerCase().includes(query) ||
      num.number?.toLowerCase().includes(query)
    );
  });

  return (
    <div className="py-5 bg-light min-vh-100">
      <div className="container-fluid px-4 px-xl-5">
        <div className="row g-4">
          {/* Credentials */}
          <div className="col-lg-5 col-xl-4">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-body" style={{ padding: "2rem" }}>
                <div className="d-flex align-items-center gap-3 mb-4">
                  <div
                    className="d-flex align-items-center justify-content-center rounded-3 flex-shrink-0"
                    style={{
                      width: 44,
                      height: 44,
                      background: "linear-gradient(135deg, #3b82f6, #2563eb)",
                      color: "white",
                    }}
                  >
                    <Gear size={20} weight="bold" />
                  </div>
                  <div>
                    <div className="fw-semibold">Configuration</div>
                    <div className="text-muted small">
                      Connect your account to start sending messages.
                    </div>
                  </div>
                </div>

                <form onSubmit={handleSubmit}>
                  {[...(app.formFields || [])]
                    .sort((a, b) =>
                      a.key === "api_key" ? -1 : b.key === "api_key" ? 1 : 0
                    )
                    .map((field, i) => {
                      const displayLabel =
                        field.key === "from_number"
                          ? "Default Phone Number"
                          : field.label;

                      return (
                        <div className="mb-3" key={i}>
                          <label
                            htmlFor={field.key}
                            className="form-label fw-medium small text-dark d-flex align-items-center"
                          >
                            {displayLabel}
                            {field.short_description && (
                              <span title={field.short_description}>
                                <Info
                                  size={14}
                                  weight="bold"
                                  className="ms-1 text-secondary"
                                  style={{ cursor: "help" }}
                                />
                              </span>
                            )}
                            {field.key === "api_key" && phoneNumbersLoading && (
                              <span
                                className="spinner-border spinner-border-sm text-primary ms-2"
                                role="status"
                                aria-hidden="true"
                              />
                            )}
                          </label>

                          {field.key === "from_number" &&
                          phoneNumbers.length > 0 ? (
                            <Select
                              inputId={field.key}
                              options={phoneOptions}
                              value={selectedPhoneOption}
                              onChange={(opt) =>
                                handlePhoneSelect(opt?.value)
                              }
                              placeholder="Search a phone number…"
                              isSearchable
                              styles={selectStyles}
                              menuPortalTarget={document.body}
                            />
                          ) : (
                            <input
                              type="text"
                              id={field.key}
                              name={field.key}
                              value={formData[field.key] || ""}
                              onChange={handleChange}
                              onBlur={
                                field.key === "api_key"
                                  ? handleApiKeyBlur
                                  : undefined
                              }
                              disabled={
                                field.key === "from_number" &&
                                (!formData.api_key || phoneNumbersLoading)
                              }
                              placeholder={
                                field.key === "from_number"
                                  ? "Enter your API key first"
                                  : `Enter ${displayLabel}`
                              }
                              className="form-control rounded-3"
                              style={{ height: "3rem" }}
                            />
                          )}

                          {field.key === "from_number" &&
                            phoneNumbers.length === 0 && (
                              <small className="text-muted">
                                {phoneNumbersLoading
                                  ? "Loading available numbers…"
                                  : "Enter your API key above to load your available numbers."}
                              </small>
                            )}
                        </div>
                      );
                    })}

                  <button
                    type="submit"
                    disabled={saving}
                    className="btn btn-primary w-100 mt-3 fw-semibold rounded-3"
                    style={{ padding: "0.85rem" }}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        />
                        Saving…
                      </>
                    ) : (
                      "Save Configuration"
                    )}
                  </button>
                </form>

                {formData.api_key && (
                  <>
                    <hr className="my-4" />
                    <div className="d-flex align-items-center gap-2 mb-3">
                      <Lightning
                        size={16}
                        weight="bold"
                        className="text-warning"
                      />
                      <span className="fw-semibold small">Webhook</span>
                    </div>

                    {webhookSetupLoading ? (
                      <div className="small p-2 border rounded-3 bg-light text-muted d-flex align-items-center gap-2">
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        />
                        Setting up webhook…
                      </div>
                    ) : formData.webhook_id ? (
                      <div className="small p-2 border rounded-3 bg-light d-flex align-items-center justify-content-between gap-2">
                        <span className="d-flex align-items-center gap-2 text-success">
                          <CheckCircle size={16} weight="fill" />
                          Webhook connected
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary"
                          onClick={ensureWebhook}
                        >
                          Reconfigure
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary w-100"
                        onClick={ensureWebhook}
                      >
                        Configure Webhook
                      </button>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>

          {/* Numbers on this account */}
          <div className="col-lg-7 col-xl-8">
            <div className="card border-0 shadow-sm rounded-4 h-100">
              <div className="card-body" style={{ padding: "2rem" }}>
                <div className="d-flex align-items-center justify-content-between mb-4">
                  <div>
                    <div className="fw-semibold">
                      Numbers on this account
                    </div>
                    <div className="text-muted small">
                      Click a number to set it as your default sender.
                      Disabling a number ignores any events coming from it.
                    </div>
                  </div>
                  {phoneNumbers.length > 0 && (
                    <span className="badge rounded-pill text-bg-light border text-secondary">
                      {phoneNumbers.length} number
                      {phoneNumbers.length === 1 ? "" : "s"}
                    </span>
                  )}
                </div>

                {phoneNumbers.length > 0 && (
                  <div className="input-group mb-3" style={{ maxWidth: 320 }}>
                    <span className="input-group-text bg-white border-end-0">
                      <MagnifyingGlass size={16} className="text-muted" />
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 ps-0"
                      placeholder="Search numbers…"
                      value={numberSearch}
                      onChange={(e) => setNumberSearch(e.target.value)}
                    />
                  </div>
                )}

                {phoneNumbers.length > 0 ? (
                  filteredPhoneNumbers.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table align-middle mb-0">
                        <thead>
                          <tr className="text-muted small text-uppercase">
                            <th
                              className="fw-semibold border-0"
                              style={{ letterSpacing: "0.03em" }}
                            >
                              Number
                            </th>
                            <th
                              className="fw-semibold border-0"
                              style={{ letterSpacing: "0.03em" }}
                            >
                              Status
                            </th>
                            <th
                              className="fw-semibold border-0"
                              style={{ letterSpacing: "0.03em" }}
                            >
                              Assigned User
                            </th>
                            <th
                              className="fw-semibold border-0"
                              style={{ letterSpacing: "0.03em" }}
                            >
                              Enabled
                            </th>
                            <th className="border-0"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredPhoneNumbers.map((num) => {
                            const isDefault =
                              formData.from_number === num.value;
                            const isWorkflowDefault =
                              formData.default_workflow_number === num.value;
                            const assignment =
                              formData.number_assignments?.[num.value] || {};
                            const isDisabled =
                              !!formData.disabled_numbers?.[num.value];
                            return (
                              <tr
                                key={num.value}
                                className={isDisabled ? "opacity-50" : ""}
                              >
                                <td>
                                  <button
                                    type="button"
                                    onClick={() => handlePhoneSelect(num.value)}
                                    className="btn p-0 border-0 bg-transparent text-start d-flex align-items-center gap-3"
                                  >
                                    <div
                                      className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                                      style={{
                                        width: 36,
                                        height: 36,
                                        background: isDefault
                                          ? "#2563eb"
                                          : "#f1f3f5",
                                        color: isDefault ? "white" : "#6c757d",
                                      }}
                                    >
                                      <Phone size={16} weight="bold" />
                                    </div>
                                    <div>
                                      <div className="fw-medium small">
                                        {num.name || "Unnamed number"}
                                      </div>
                                      <div className="text-muted small">
                                        {num.formattedNumber || num.number}
                                      </div>
                                    </div>
                                  </button>
                                </td>
                                <td>
                                  <div className="d-flex flex-column gap-1" style={{ width: "fit-content" }}>
                                    {isDefault && (
                                      <span className="badge rounded-pill text-bg-primary d-flex align-items-center gap-1">
                                        <CheckCircle size={12} weight="fill" />
                                        Default
                                      </span>
                                    )}
                                    {isWorkflowDefault && (
                                      <span className="badge rounded-pill text-bg-warning text-dark d-flex align-items-center gap-1">
                                        <CheckCircle size={12} weight="fill" />
                                        Workflow Default
                                      </span>
                                    )}
                                  </div>
                                </td>
                                <td>
                                  <span className="small">
                                    {assignment.assignedUsers?.length > 0 ? (
                                      assignment.assignedUsers
                                        .map((u) => u.label)
                                        .join(", ")
                                    ) : (
                                      <span className="text-muted">—</span>
                                    )}
                                  </span>
                                </td>
                                <td>
                                  <div className="form-check form-switch mb-0">
                                    <input
                                      className="form-check-input"
                                      type="checkbox"
                                      role="switch"
                                      id={`enabled_${num.value}`}
                                      checked={!isDisabled}
                                      onChange={() =>
                                        toggleNumberDisabled(num.value)
                                      }
                                    />
                                  </div>
                                </td>
                                <td className="text-end">
                                  <button
                                    type="button"
                                    onClick={() => openAssignModal(num)}
                                    disabled={isDisabled}
                                    title={
                                      isDisabled
                                        ? "Enable this number to configure it"
                                        : "Configure number"
                                    }
                                    className="btn btn-light rounded-circle d-flex align-items-center justify-content-center p-0 ms-auto"
                                    style={{ width: 30, height: 30 }}
                                  >
                                    <Gear size={14} weight="bold" />
                                  </button>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center text-muted small py-4">
                      No numbers match "{numberSearch}".
                    </div>
                  )
                ) : (
                  <div className="d-flex flex-column align-items-center justify-content-center text-center py-5">
                    <div
                      className="d-flex align-items-center justify-content-center rounded-circle mb-3"
                      style={{
                        width: 56,
                        height: 56,
                        background: "#f1f3f5",
                        color: "#6c757d",
                      }}
                    >
                      <Phone size={24} weight="bold" />
                    </div>
                    <div className="fw-medium">
                      {phoneNumbersLoading
                        ? "Loading your numbers…"
                        : "No numbers loaded yet"}
                    </div>
                    <div className="text-muted small" style={{ maxWidth: 320 }}>
                      {phoneNumbersLoading
                        ? "This will only take a moment."
                        : "Enter your API key on the left to see every number on your account here."}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {assignModalNumber && (
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content border-0 shadow-lg rounded-4">
              <div className="modal-header border-bottom-0 pb-0">
                <h5 className="modal-title fw-bold text-dark">
                  Assign {assignModalNumber.formattedNumber || assignModalNumber.number}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setAssignModalNumber(null)}
                ></button>
              </div>

              <div className="modal-body pt-3">
                <div className="row g-4">
                  <div className="col-md-6">
                    <div className="fw-semibold mb-3">User assignment</div>

                    <div className="form-check mb-3">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="useAsDefaultUserNumber"
                        checked={assignForm.useAsDefaultUserNumber}
                        onChange={(e) =>
                          setAssignForm({
                            ...assignForm,
                            useAsDefaultUserNumber: e.target.checked,
                          })
                        }
                      />
                      <label
                        className="form-check-label small"
                        htmlFor="useAsDefaultUserNumber"
                      >
                        Use as default user number
                      </label>
                    </div>

                    <label className="form-label small fw-medium">
                      Assigned user
                    </label>
                    <AsyncSelect
                      key={assignModalNumber?.value || "assigned-user"}
                      cacheOptions
                      defaultOptions
                      loadOptions={loadLocationUsers}
                      value={assignForm.assignedUsers}
                      onChange={(opts) =>
                        setAssignForm({ ...assignForm, assignedUsers: opts })
                      }
                      placeholder="Search users…"
                      isMulti
                      styles={selectStyles}
                      menuPortalTarget={document.body}
                    />
                  </div>

                  <div className="col-md-6">
                    <div className="fw-semibold mb-3">
                      Workflow assignment
                    </div>

                    <div className="form-check">
                      <input
                        type="checkbox"
                        className="form-check-input"
                        id="useAsDefaultWorkflowNumber"
                        checked={assignForm.useAsDefaultWorkflowNumber}
                        onChange={(e) =>
                          setAssignForm({
                            ...assignForm,
                            useAsDefaultWorkflowNumber: e.target.checked,
                          })
                        }
                      />
                      <label
                        className="form-check-label small"
                        htmlFor="useAsDefaultWorkflowNumber"
                      >
                        Use as default workflow number
                      </label>
                    </div>
                  </div>
                </div>

              </div>

              <div className="modal-footer border-top-0 pt-0 d-flex justify-content-end gap-2">
                <button
                  type="button"
                  className="btn btn-light px-4"
                  onClick={() => setAssignModalNumber(null)}
                  disabled={savingAssignment}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary px-4"
                  onClick={handleAssignSave}
                  disabled={savingAssignment}
                >
                  {savingAssignment ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Saving…
                    </>
                  ) : (
                    "Save"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Configuration;
